import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
export interface EzremsTransitionCommand {
  command_id: string;
  target: 'MIDTERM' | 'GENERAL';
  expected_sale_revision: number;
  first_move_in_date: string | null;
  daily_price_minor?: number;
  reason: string;
}
export interface EzremsTransitionStatus {
  command_id: string;
  room_id: string;
  target: 'MIDTERM' | 'GENERAL';
  outcome: 'PENDING' | 'UNKNOWN' | 'APPLIED' | 'NOT_APPLIED';
  transport: 'UNKNOWN' | 'DELIVERED';
  readback_matches: boolean | null;
  reason:
    | 'AWAITING_RESULT'
    | 'TRANSPORT_UNKNOWN'
    | 'READBACK_UNAVAILABLE'
    | 'READBACK_MISMATCH'
    | 'FINALITY_UNCONFIRMED'
    | null;
  current_sale_revision: number;
  pending: boolean;
}
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '외부 판매 요청 상태를 확인하지 못했습니다.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
const revision = (v: unknown) => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
export async function submitEzremsTransition(
  client: ServiceClient,
  roomId: string,
  command: EzremsTransitionCommand,
): Promise<void> {
  if (
    !isUuid(roomId) ||
    !['MIDTERM', 'GENERAL'].includes(command.target) ||
    !isUuid(command.command_id) ||
    !exact({ ...command }, [
      'command_id',
      'target',
      'expected_sale_revision',
      'first_move_in_date',
      'reason',
      ...(Object.hasOwn(command, 'daily_price_minor') ? ['daily_price_minor'] : []),
    ]) ||
    !revision(command.expected_sale_revision) ||
    typeof command.reason !== 'string' ||
    !command.reason.trim() ||
    Array.from(command.reason).length > 500 ||
    (command.target === 'GENERAL'
      ? command.first_move_in_date !== null || Object.hasOwn(command, 'daily_price_minor')
      : (Object.hasOwn(command, 'daily_price_minor') &&
          (!Number.isSafeInteger(command.daily_price_minor) ||
            Number(command.daily_price_minor) <= 0)) ||
        typeof command.first_move_in_date !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}$/.test(command.first_move_in_date) ||
        !isOffsetDateTime(`${command.first_move_in_date}T00:00:00Z`))
  )
    throw invalid();
  const v = await client.post<unknown>(`admin/rooms/${roomId}/ezrems-transition`, {
    json: command,
  });
  // ServiceClient strips the HTTP status. This validates the receipt, never final success.
  if (
    !record(v) ||
    !exact(v, [
      'command_id',
      'room_id',
      'revision',
      'product',
      'channel',
      'first_move_in_date',
      'provider_outcome',
    ]) ||
    v.command_id !== command.command_id ||
    v.room_id !== roomId ||
    !revision(v.revision) ||
    !['midterm', 'long_term'].includes(String(v.product)) ||
    v.channel !== 'own_website' ||
    !(v.first_move_in_date === null || typeof v.first_move_in_date === 'string') ||
    v.provider_outcome !== 'unknown'
  )
    throw invalid();
}
export async function readEzremsTransitionCommand(
  client: ServiceClient,
  roomId: string,
  commandId: string,
  target: EzremsTransitionCommand['target'],
): Promise<EzremsTransitionStatus> {
  if (!isUuid(roomId) || !isUuid(commandId)) throw invalid();
  const v = await client.get<unknown>(
    `admin/rooms/${roomId}/ezrems-transition-commands/${commandId}`,
    { headers: { 'Cache-Control': 'no-store' } },
  );
  if (
    !record(v) ||
    !exact(v, [
      'command_id',
      'room_id',
      'target',
      'outcome',
      'transport',
      'readback_matches',
      'reason',
      'current_sale_revision',
      'pending',
    ]) ||
    !isUuid(v.command_id) ||
    v.command_id.toLowerCase() !== commandId.toLowerCase() ||
    !isUuid(v.room_id) ||
    v.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !['MIDTERM', 'GENERAL'].includes(target) ||
    v.target !== target ||
    !['PENDING', 'UNKNOWN', 'APPLIED', 'NOT_APPLIED'].includes(String(v.outcome)) ||
    !['UNKNOWN', 'DELIVERED'].includes(String(v.transport)) ||
    !(v.readback_matches === null || typeof v.readback_matches === 'boolean') ||
    !(
      v.reason === null ||
      [
        'AWAITING_RESULT',
        'TRANSPORT_UNKNOWN',
        'READBACK_UNAVAILABLE',
        'READBACK_MISMATCH',
        'FINALITY_UNCONFIRMED',
      ].includes(typeof v.reason === 'string' ? v.reason : '')
    ) ||
    !revision(v.current_sale_revision) ||
    typeof v.pending !== 'boolean'
  )
    throw invalid();
  return v as unknown as EzremsTransitionStatus;
}
